import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Badge — a small inline label for a status or a highlight, in every app
 * (ADR-0088 D2). Its text is the status, so colour only reinforces it.
 *
 * The four feedback states, plus two that label rather than judge:
 *
 * - `neutral`: a fact worth labeling. "Internal", "Trial".
 * - `accent`: a highlight. "Recommended", "Current".
 * - `success`: a confirmed positive outcome. "Passed", "Completed".
 * - `warning`: missing or uncertain evidence, or a limit. "From B1".
 * - `danger`: something to act on. "Failed", "Overdue".
 * - `muted`: a deliberate, benign state. "Canceled", "Disabled".
 *
 * The four states take the family's look (feedback.tsx), the tint and
 * foreground a notice and a toast use: every coloured badge writes its text in
 * a darker shade of its own hue, dark enough for its 12px text to clear WCAG
 * AA on its tint. `accent` does the same with the accent; `neutral` and
 * `muted` are greys.
 *
 * Two shapes and two edges, as options: the rounded label (Admin, dense in
 * a table) or the pill (the learner app, softer beside customer copy); and
 * the border, which defines a badge on a busy row, or none, the calmer look
 * beside prose on a white card.
 *
 * Interactive chips (a choice in a group, a filter) are buttons, not badges.
 */
import { cva } from 'class-variance-authority';
import { cn } from './cn.js';
import { FEEDBACK_FOREGROUND, FEEDBACK_TINT } from './feedback.js';
const badgeVariants = cva(
// `gap-1` lays out a badge that carries a small leading icon.
'inline-flex items-center gap-1 whitespace-nowrap border py-0.5 text-label font-medium', {
    // cva names every axis a variant. `variant` is the badge's meaning; the
    // others (`uppercase`, `pill`, `bordered`) are options on any of them.
    variants: {
        variant: {
            neutral: cn(FEEDBACK_TINT.neutral, 'text-text-primary'),
            accent: 'border-accent bg-accent-soft text-text-primary',
            success: cn(FEEDBACK_TINT.success, FEEDBACK_FOREGROUND.success),
            warning: cn(FEEDBACK_TINT.warning, FEEDBACK_FOREGROUND.warning),
            danger: cn(FEEDBACK_TINT.danger, FEEDBACK_FOREGROUND.danger),
            muted: 'border-border-default text-text-secondary',
        },
        // Capitals for a short label that tags a heading ("CURRENT" beside a
        // study period's title). Set by CSS, so the text, and what a screen
        // reader reads, stays as written. Capitals read larger than lowercase,
        // so they are smaller and bolder, on the label's 16px line: 10.5px, a
        // recorded optical exception to the type roles (design-tokens.md §2.1).
        uppercase: { true: 'text-[10.5px] leading-4 font-bold uppercase tracking-[0.02em]' },
        // The pill: the learner app's label shape, softer beside customer copy.
        // The rounded label is Admin's, denser in a table.
        pill: { true: 'rounded-token-pill px-2', false: 'rounded px-1.5' },
        // Borderless keeps a transparent border, so the badge is the same size
        // either way. Defined after `variant`, so it overrides the border colour.
        bordered: { true: '', false: 'border-transparent' },
    },
    // Muted has no fill: its border is its only edge, so it keeps it.
    compoundVariants: [{ variant: 'muted', bordered: false, class: 'border-border-default' }],
    defaultVariants: { variant: 'neutral', pill: false, bordered: true },
});
export function Badge({ className, variant, uppercase = false, pill = false, bordered = true, ...props }) {
    return (_jsx("span", { "data-slot": "badge", "data-variant": variant ?? 'neutral', className: cn(badgeVariants({ variant, uppercase, pill, bordered }), className), ...props }));
}
export { badgeVariants };
//# sourceMappingURL=badge.js.map